import React, { useEffect, useState } from "react";
import { adminApi } from "./api";
import { Inbox, Mail, Sparkles, Download, ExternalLink } from "lucide-react";

const TABS = [
  { value: "contact", label: "Contact form", icon: Mail },
  { value: "newsletter", label: "Newsletter", icon: Inbox },
  { value: "subscriptions", label: "Subscriptions", icon: Sparkles },
];

const FORWARD_TO = "contact@mediproexpress.com";

function toCSV(rows, headers) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [headers.map(esc).join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
}
function downloadCSV(name, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN"); } catch { return iso; }
};

const replyHref = (email, name, original) => {
  const subject = encodeURIComponent(`Re: Your BananaX enquiry`);
  const body = encodeURIComponent(
    `Hi ${name || "there"},\n\nThank you for reaching out to BananaX.\n\n— BananaX Concierge\n\n--- Original message ---\n${original || ""}`
  );
  return `mailto:${email}?subject=${subject}&body=${body}&cc=${FORWARD_TO}`;
};

export default function AdminMessages() {
  const [tab, setTab] = useState("contact");
  const [summary, setSummary] = useState({ contact: 0, newsletter: 0, subscriptions: 0 });
  const [contact, setContact] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      adminApi.get("/messages/summary"),
      adminApi.get("/messages/contact"),
      adminApi.get("/messages/newsletter"),
      adminApi.get("/messages/subscriptions"),
    ]).then(([s, c, n, sub]) => {
      setSummary(s.data);
      setContact(c.data.messages);
      setNewsletter(n.data.subscribers);
      setSubscriptions(sub.data.subscriptions);
    }).finally(() => setLoading(false));

    // Mark everything read on open + tell the sidebar to refresh the badge
    adminApi.post("/messages/mark-read?source=all").then(() => {
      window.dispatchEvent(new CustomEvent("admin-messages-read"));
    }).catch(() => { /* ignore */ });
  }, []);

  const exportNewsletter = () => {
    const csv = toCSV(newsletter.map((s) => ({ email: s.email, signed_up: s.created_at })), ["email", "signed_up"]);
    downloadCSV(`newsletter-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };
  const exportContact = () => {
    const csv = toCSV(
      contact.map((m) => ({ name: m.name, email: m.email, message: m.message, received: m.created_at })),
      ["name", "email", "message", "received"]
    );
    downloadCSV(`contact-messages-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };
  const exportSubs = () => {
    const csv = toCSV(
      subscriptions.map((s) => ({ name: s.name, email: s.email, plan: s.plan, status: s.status, requested: s.created_at })),
      ["name", "email", "plan", "status", "requested"]
    );
    downloadCSV(`subscription-leads-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  return (
    <div className="space-y-6" data-testid="admin-messages-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="label-eyebrow text-gold">Storefront inbox</p>
          <h1 className="font-serif text-4xl text-bone mt-1">Messages</h1>
          <p className="text-mist text-sm font-light mt-2 max-w-2xl">
            All submissions from the public site land here. Use the <span className="text-gold">Reply</span> button to draft a response from your default mail client — every reply is auto-CC&apos;d to <span className="text-gold">{FORWARD_TO}</span>.
          </p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid sm:grid-cols-3 gap-4">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={`text-left bg-ink-900 border p-5 transition-colors ${tab === t.value ? "border-gold" : "border-white/5 hover:border-gold/40"}`}
            data-testid={`messages-tab-${t.value}`}
          >
            <div className="flex items-center justify-between">
              <p className="label-eyebrow text-mist flex items-center gap-2">
                <t.icon className="w-3.5 h-3.5 text-gold" /> {t.label}
              </p>
            </div>
            <p className="num-display text-3xl text-bone mt-3">{summary[t.value] || 0}</p>
            <p className="text-xs text-mist font-light">total {t.value === "subscriptions" ? "leads" : "received"}</p>
          </button>
        ))}
      </div>

      {loading && <p className="text-mist">Loading…</p>}

      {/* Contact form messages */}
      {!loading && tab === "contact" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={exportContact} disabled={contact.length === 0} className="btn-ghost !py-2.5 disabled:opacity-40" data-testid="export-contact-csv">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
          {contact.length === 0 ? (
            <p className="text-mist italic font-light text-sm py-8 text-center bg-ink-900 border border-white/5">No contact messages yet.</p>
          ) : (
            <div className="space-y-3" data-testid="contact-messages-list">
              {contact.map((m) => (
                <article key={m.id} className="bg-ink-900 border border-white/5 p-5" data-testid={`contact-msg-${m.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-bone font-light">{m.name}</p>
                      <p className="text-mist text-xs">{m.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="label-eyebrow text-mist text-xs">{fmtDate(m.created_at)}</p>
                      <a
                        href={replyHref(m.email, m.name, m.message)}
                        className="btn-primary !py-2 !px-3 text-xs"
                        data-testid={`reply-contact-${m.id}`}
                      >
                        <ExternalLink className="w-3 h-3" /> Reply
                      </a>
                    </div>
                  </div>
                  <p className="text-bone/90 font-light mt-4 leading-relaxed whitespace-pre-wrap text-sm">{m.message}</p>
                </article>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Newsletter subscribers */}
      {!loading && tab === "newsletter" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={exportNewsletter} disabled={newsletter.length === 0} className="btn-ghost !py-2.5 disabled:opacity-40" data-testid="export-newsletter-csv">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
          {newsletter.length === 0 ? (
            <p className="text-mist italic font-light text-sm py-8 text-center bg-ink-900 border border-white/5">No newsletter signups yet.</p>
          ) : (
            <div className="bg-ink-900 border border-white/5 overflow-x-auto" data-testid="newsletter-table">
              <table className="w-full text-sm">
                <thead className="bg-ink-950">
                  <tr className="text-left label-eyebrow text-mist">
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Signed up</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {newsletter.map((n) => (
                    <tr key={n.id || n.email} className="border-t border-white/5 text-bone/90 font-light" data-testid={`newsletter-row-${n.email}`}>
                      <td className="px-4 py-3">{n.email}</td>
                      <td className="px-4 py-3 text-mist text-xs">{fmtDate(n.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <a href={`mailto:${n.email}?cc=${FORWARD_TO}`} className="text-mist hover:text-gold label-eyebrow text-xs">
                          Compose
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Subscription leads */}
      {!loading && tab === "subscriptions" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={exportSubs} disabled={subscriptions.length === 0} className="btn-ghost !py-2.5 disabled:opacity-40" data-testid="export-subscriptions-csv">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
          {subscriptions.length === 0 ? (
            <p className="text-mist italic font-light text-sm py-8 text-center bg-ink-900 border border-white/5">No subscription leads yet.</p>
          ) : (
            <div className="bg-ink-900 border border-white/5 overflow-x-auto" data-testid="subscriptions-table">
              <table className="w-full text-sm">
                <thead className="bg-ink-950">
                  <tr className="text-left label-eyebrow text-mist">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Requested</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((s) => (
                    <tr key={s.id} className="border-t border-white/5 text-bone/90 font-light" data-testid={`sub-row-${s.id}`}>
                      <td className="px-4 py-3">{s.name || "—"}</td>
                      <td className="px-4 py-3 text-mist text-xs">{s.email}</td>
                      <td className="px-4 py-3 text-gold label-eyebrow">{s.plan}</td>
                      <td className="px-4 py-3 text-mist text-xs">{s.status}</td>
                      <td className="px-4 py-3 text-mist text-xs">{fmtDate(s.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <a
                          href={`mailto:${s.email}?subject=${encodeURIComponent("Welcome to BananaX " + (s.plan || "") + " plan")}&cc=${FORWARD_TO}`}
                          className="text-mist hover:text-gold label-eyebrow text-xs"
                        >
                          Onboard
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
